import { config } from '../../config/env';

export class ApiClient {
    private baseURL: string;
    private timeout: number;

    constructor(baseURL?: string) {
        this.baseURL = baseURL || config.api.baseUrl;
        this.timeout = config.api.timeout;
    }

    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const url = `${this.baseURL}${endpoint}`;
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    ...options.headers,
                },
            });

            clearTimeout(timeoutId);

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            return data;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    throw new Error('Request timeout');
                }
                throw error;
            }
            throw new Error('Unknown error occurred');
        }
    }

    async get<T>(endpoint: string, token?: string): Promise<T> {
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return this.request<T>(endpoint, {
            method: 'GET',
            headers,
        });
    }

    async post<T>(endpoint: string, body: unknown, token?: string): Promise<T> {
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return this.request<T>(endpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });
    }

    async put<T>(endpoint: string, body: unknown, token?: string): Promise<T> {
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return this.request<T>(endpoint, {
            method: 'PUT',
            headers,
            body: JSON.stringify(body),
        });
    }

    async delete<T>(endpoint: string, token?: string): Promise<T> {
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return this.request<T>(endpoint, {
            method: 'DELETE',
            headers,
        });
    }
}

export const apiClient = new ApiClient();

